import React, { Component } from 'react';
import { HashRouter, Route, Switch } from 'react-router-dom';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import i18n from 'i18next'
import './App.scss';
import packageJson from '../package.json';
import {
  isNewBuildAvailable,
  CHECK_INTERVAL_MS,
  AUTO_RELOAD_SECONDS,
  targetBuildId,
  shouldAutoReload,
  markAutoReload,
  isChunkLoadError,
} from './utils/versionCheck';

 global.appVersion = packageJson.version;

const loading = () => (
  <div className="animated fadeIn pt-3 text-center">
    <div className="sk-spinner sk-spinner-pulse" />
  </div>
);

// Containers
const DefaultLayout = React.lazy(() => import('./containers/DefaultLayout'));
const SuperAdminLayout = React.lazy(() =>
  import('./containers/SuperAdminLayout/SuperAdminLayout')
);

// Pages
const Login = React.lazy(() => import('./views/Pages/Login'));
const Register = React.lazy(() => import('./views/Pages/Register'));
const Page404 = React.lazy(() => import('./views/Pages/Page404'));
const Page500 = React.lazy(() => import('./views/Pages/Page500'));
const WaitngForRedirectPage=React.lazy(() =>
import('./views/WaitngForRedirectPage/WaitngForRedirectPage')
);
const ForgotPassword = React.lazy(() => import('./views/Pages/ForgetPassword'));
const ForgetPasswordOTP = React.lazy(() =>
  import('./views/Pages/ForgetPasswordOTP')
);
const ChangePassword = React.lazy(() => import('./views/Pages/ChangePassword'));

class App extends Component {
  constructor(props) {

          super(props);
    
    this.state = {
      isLoading: true,
      isLatestVersionAvailable: false,
      secondsLeft: AUTO_RELOAD_SECONDS,
    };
  }

  // Compare the server's /meta.json with the build running in this tab. Never
  // throws: a missing or unreadable file just means "no update known".
  checkForNewBuild = () => {
    fetch(`/meta.json?${new Date().getTime()}`, { cache: 'no-store' })
      .then((response) => response.json())
      .then((meta) => {
        const available = isNewBuildAvailable(meta, {
          version: global.appVersion,
          buildId: process.env.REACT_APP_BUILD_ID,
        });
        this.targetId = targetBuildId(meta);
        if (available && !this.state.isLatestVersionAvailable) {
          this.setState({ isLatestVersionAvailable: true, secondsLeft: AUTO_RELOAD_SECONDS });
          this.startCountdown();
        } else if (!available && this.state.isLatestVersionAvailable) {
          this.setState({ isLatestVersionAvailable: false });
          clearInterval(this.countdownTimer);
        }
      })
      .catch(() => {});
  };

  onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      this.checkForNewBuild();
    }
  };

  // Reload now. Automatic reloads happen at most once per advertised build (see
  // shouldAutoReload), so a stale cached page can never turn this into a loop; the
  // user's own "Update now" click always goes through.
  reloadToNewBuild = (manual) => {
    if (!manual) {
      if (!shouldAutoReload(this.targetId, window.sessionStorage)) {
        return;
      }
      markAutoReload(this.targetId, window.sessionStorage);
    }
    const reload = () => window.location.reload();
    if (typeof caches !== 'undefined' && caches.keys) {
      caches.keys()
        .then((names) => Promise.all(names.map((name) => caches.delete(name))))
        .then(reload, reload);
    } else {
      reload();
    }
  };

  refreshToNewBuild = () => this.reloadToNewBuild(true);

  startCountdown = () => {
    clearInterval(this.countdownTimer);
    this.countdownTimer = setInterval(() => {
      const secondsLeft = this.state.secondsLeft - 1;
      if (secondsLeft <= 0) {
        clearInterval(this.countdownTimer);
        this.setState({ secondsLeft: 0 });
        this.reloadToNewBuild(false);
      } else {
        this.setState({ secondsLeft });
      }
    }, 1000);
  };

  // Leaving a page is the safest moment to update: nothing on it is lost, and the hash
  // in the URL keeps the user on the page they clicked.
  onHashChange = () => {
    if (this.state.isLatestVersionAvailable) {
      this.reloadToNewBuild(false);
    }
  };

  // A page file that no longer exists on the server means this tab is on an old build.
  onChunkError = (event) => {
    const error = (event && (event.reason || event.error)) || event;
    if (isChunkLoadError(error)) {
      this.targetId = this.targetId || 'chunk-error';
      this.reloadToNewBuild(false);
    }
  };

  componentWillUnmount() {
    clearInterval(this.versionTimer);
    clearInterval(this.countdownTimer);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('hashchange', this.onHashChange);
    window.removeEventListener('unhandledrejection', this.onChunkError);
    window.removeEventListener('error', this.onChunkError);
  }

    componentDidMount() {
        i18n.changeLanguage(localStorage.getItem('language')?localStorage.getItem('language'):'da')
        this.checkForNewBuild();
        this.versionTimer = setInterval(this.checkForNewBuild, CHECK_INTERVAL_MS);
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        window.addEventListener('hashchange', this.onHashChange);
        window.addEventListener('unhandledrejection', this.onChunkError);
        window.addEventListener('error', this.onChunkError);
    }

  render() {
    const { userInfo } = this.props;
    document.title = process.env.REACT_APP_META_TITLE;
    const { isLatestVersionAvailable, secondsLeft } = this.state;

    return (
      <>
      {isLatestVersionAvailable && (
        <div className="new-version-banner" role="alert">
          <span>{i18n.t('global.newVersionCountdown', { seconds: secondsLeft })}</span>
          <button type="button" onClick={this.refreshToNewBuild}>
            {i18n.t('global.newVersionNow')}
          </button>
        </div>
      )}
      <HashRouter>
        <React.Suspense fallback={loading()}>
          <Switch>
            <Route
              exact
              path="/login"
              name="Login Page"
              render={(props) => <Login {...props} />}
            />
            <Route
              exact
              path="/register"
              name="Register Page"
              render={(props) => <Register {...props} />}
            />
            <Route
              exact
              path="/forgetpasswordotp"
              name="Forgot Password OTP"
              render={(props) => <ForgetPasswordOTP {...props} />}
            />
            <Route
              exact
              path="/forgetpassword"
              name="Forgot Password"
              render={(props) => <ForgotPassword {...props} />}
            />
            <Route
              exact
              path="/changepassword"
              name="Change Password"
              render={(props) => <ChangePassword {...props} />}
            />
            <Route
              exact
              path="/404"
              name="Page 404"
              render={(props) => <Page404 {...props} />}
            />
            <Route
              exact
              path="/500"
              name="Page 500"
              render={(props) => <Page500 {...props} />}
            />
             <Route
              exact
              path="/redirecting/:userToken"
              name="Redirectiong"
              render={(props) => <WaitngForRedirectPage {...props} />}
            />
            <Route
              path="/"
              name="Home"
              render={(props) =>
                userInfo && userInfo.is_supper_admin === 0 ? (
                  <DefaultLayout {...props} />
                ) : (
                  <SuperAdminLayout {...props} />
                )
              }
            />
          </Switch>
        </React.Suspense>
      </HashRouter>
      </>
    );
  }
}

//export default App

const mapStateToProps = (state) => ({
  userInfo: state.userInfo.userInfo,
});

export default withTranslation()(connect(mapStateToProps, {})(App));
